import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuthContext } from '@/contexts/AuthContext';
import { useSiteSettings } from '@/hooks/useSiteSettings';

export default function LandingNav() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuthContext();
  const { settings: siteSettings } = useSiteSettings();
  const siteLogo = siteSettings.site_logo_url ?? '';
  const siteName = siteSettings.site_name || 'API MART';
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (id: string) => {
    setMobileOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-md border-b border-gray-100' : 'bg-transparent'}`}>
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        {/* Logo — goes to dashboard if logged in, scrolls to top otherwise */}
        <button
          onClick={() => isAuthenticated ? navigate('/dashboard') : window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="flex items-center gap-2.5 cursor-pointer"
        >
          {siteLogo ? (
            <img
              src={siteLogo}
              alt={siteName}
              className="h-9 max-w-[160px] object-contain"
            />
          ) : (
            <>
              <div className="w-8 h-8 bg-brand-500 rounded-lg flex items-center justify-center">
                <i className="ri-code-box-line text-white text-base" />
              </div>
              <span className={`font-bold text-base tracking-wide ${scrolled ? 'text-gray-900' : 'text-white'}`}>{siteName}</span>
            </>
          )}
        </button>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-7">
          {[
            { label: 'Features', id: 'features' },
            { label: 'APIs', id: 'apis' },
            { label: 'Pricing', id: 'pricing' },
            { label: 'Reviews', id: 'reviews' },
          ].map(item => (
            <button
              key={item.id}
              onClick={() => scrollTo(item.id)}
              className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${scrolled ? 'text-gray-600 hover:text-gray-900' : 'text-white/80 hover:text-white'}`}
            >
              {item.label}
            </button>
          ))}
          <button
            onClick={() => navigate('/marketplace')}
            className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${scrolled ? 'text-gray-600 hover:text-gray-900' : 'text-white/80 hover:text-white'}`}
          >
            Marketplace
          </button>
        </nav>

        {/* Desktop CTA */}
        <div className="hidden md:flex items-center gap-3">
          {isAuthenticated ? (
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg bg-brand-500 hover:bg-brand-600 text-white cursor-pointer whitespace-nowrap transition-colors"
            >
              <i className="ri-dashboard-line" />
              {user?.role === 'admin' ? 'Admin Panel' : 'Dashboard'}
            </button>
          ) : (
            <>
              <button
                onClick={() => navigate('/login')}
                className={`text-sm font-medium px-4 py-2 rounded-lg cursor-pointer whitespace-nowrap transition-colors ${scrolled ? 'text-gray-600 hover:text-gray-900' : 'text-white/80 hover:text-white'}`}
              >
                Sign In
              </button>
              <button
                onClick={() => navigate('/register')}
                className="text-sm font-semibold px-4 py-2 rounded-lg bg-brand-500 hover:bg-brand-600 text-white cursor-pointer whitespace-nowrap transition-colors"
              >
                Get Started
              </button>
            </>
          )}
        </div>

        {/* Mobile Hamburger */}
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className={`md:hidden cursor-pointer w-8 h-8 flex items-center justify-center rounded-lg ${scrolled ? 'text-gray-700' : 'text-white'}`}
        >
          <i className={mobileOpen ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'} />
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="md:hidden bg-white border-t border-gray-100 px-6 py-4 space-y-3"
        >
          {['features', 'apis', 'pricing', 'reviews'].map(id => (
            <button
              key={id}
              onClick={() => scrollTo(id)}
              className="block w-full text-left text-sm font-medium text-gray-700 capitalize py-2 cursor-pointer"
            >
              {id}
            </button>
          ))}
          <button
            onClick={() => { setMobileOpen(false); navigate('/marketplace'); }}
            className="block w-full text-left text-sm font-medium text-gray-700 py-2 cursor-pointer"
          >
            Marketplace
          </button>
          <div className="flex gap-3 pt-2">
            {isAuthenticated ? (
              <button
                onClick={() => { setMobileOpen(false); navigate('/dashboard'); }}
                className="flex-1 py-2.5 bg-brand-500 text-white rounded-lg text-sm font-semibold cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
              >
                <i className="ri-dashboard-line" />Dashboard
              </button>
            ) : (
              <>
                <button
                  onClick={() => navigate('/login')}
                  className="flex-1 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-600 cursor-pointer whitespace-nowrap"
                >
                  Sign In
                </button>
                <button
                  onClick={() => navigate('/register')}
                  className="flex-1 py-2.5 bg-brand-500 text-white rounded-lg text-sm font-semibold cursor-pointer whitespace-nowrap"
                >
                  Get Started
                </button>
              </>
            )}
          </div>
        </motion.div>
      )}
    </header>
  );
}
